import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  TextField,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { WizardDialog } from '../WizardDialog';
import { WizardStep } from '../WizardStep';
import { useWizard } from '../../contexts/WizardContext';
import { ProgressSummaryPanel, SummarySection } from '../wizard-panels/ProgressSummaryPanel';
import { rangeosApiService } from '../../services/rangeos-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../../services/rangeos-environment-api';
import { basePrefixApi } from '../../services/api';

export interface ImportDraftFromS3WizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (jobId: string) => void;
}

const wizardSteps = [
  { label: 'Select Source', description: 'Choose S3 drafts to import' },
  { label: 'Select Destination', description: 'Choose target RangeOS' },
  { label: 'Configure Import', description: 'Set import options' },
  { label: 'Review & Import', description: 'Review and start import' }
];

// Step 1: Select S3 Source
const SourceSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<any[]>([]);
  const [draftFiles, setDraftFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const envs = await basePrefixApi.getEnvironmentsWithBuckets();
        setEnvironments(envs);
      } catch (error) {
        console.error('Failed to load environments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, []);

  useEffect(() => {
    const loadDraftFiles = async () => {
      const sourceEnvironment = wizard.getData('sourceEnvironment');
      if (!sourceEnvironment) return;

      setLoadingFiles(true);
      try {
        const files = await rangeosApiService.listDraftFilesFromS3(sourceEnvironment);
        setDraftFiles(files);
      } catch (error) {
        console.error('Failed to load draft files:', error);
      } finally {
        setLoadingFiles(false);
      }
    };

    loadDraftFiles();
  }, [wizard.getData('sourceEnvironment')]);

  // Initialize selected files from wizard data
  useEffect(() => {
    const saved = wizard.getData('selectedFiles', []);
    setSelectedFiles(saved);
  }, []);

  // Update validation whenever selection changes
  useEffect(() => {
    const sourceEnvironmentId = wizard.getData('sourceEnvironmentId');
    const isValid = Boolean(sourceEnvironmentId && selectedFiles.length > 0);
    wizard.setStepValidation(0, isValid);
  }, [selectedFiles, wizard.getData('sourceEnvironmentId')]);

  const handleFileToggle = (fileKey: string) => {
    const newSelection = selectedFiles.includes(fileKey)
      ? selectedFiles.filter(k => k !== fileKey)
      : [...selectedFiles, fileKey];

    setSelectedFiles(newSelection);
    wizard.setData('selectedFiles', newSelection);

    // Update file names for display
    const names = newSelection
      .map(key => draftFiles.find(f => f.key === key)?.name)
      .filter(Boolean);
    wizard.setData('selectedFileNames', names);
  };

  const handleSelectAll = () => {
    const filesToSelect = filteredFiles;
    const allFilteredKeys = filesToSelect.map(f => f.key);

    // Check if all filtered files are selected
    const allFilteredSelected = allFilteredKeys.every(key => selectedFiles.includes(key));

    if (allFilteredSelected) {
      // Deselect all filtered files
      const newSelection = selectedFiles.filter(key => !allFilteredKeys.includes(key));
      setSelectedFiles(newSelection);
      wizard.setData('selectedFiles', newSelection);
      const names = newSelection.map(key => draftFiles.find(f => f.key === key)?.name).filter(Boolean);
      wizard.setData('selectedFileNames', names);
    } else {
      // Select all filtered files
      const newSelection = [...new Set([...selectedFiles, ...allFilteredKeys])];
      setSelectedFiles(newSelection);
      wizard.setData('selectedFiles', newSelection);
      const names = newSelection.map(key => draftFiles.find(f => f.key === key)?.name).filter(Boolean);
      wizard.setData('selectedFileNames', names);
    }
  };

  // Filter draft files based on search query
  const filteredFiles = draftFiles.filter(file => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      file.name?.toLowerCase().includes(query) ||
      file.key?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select S3 Drafts to Import
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose the S3 environment and draft files you want to import.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>S3 Environment</InputLabel>
        <Select
          value={wizard.getData('sourceEnvironmentId', '')}
          onChange={(e) => {
            const env = environments.find(env => env.environmentId === e.target.value);
            wizard.setData('sourceEnvironmentId', e.target.value);
            wizard.setData('sourceEnvironment', env?.prefix || ''); // Store prefix for backend
            setSelectedFiles([]);
            wizard.setData('selectedFiles', []);
          }}
          label="S3 Environment"
        >
          {environments.map((env) => (
            <MenuItem key={env.environmentId} value={env.environmentId}>
              {env.environmentName} ({env.prefix}) - {env.bucketConfig.bucket}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {wizard.getData('sourceEnvironment') && (
        <Box>
          {loadingFiles ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading draft files...</Typography>
            </Box>
          ) : draftFiles.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No draft files found in this environment
            </Alert>
          ) : (
            <>
              <TextField
                fullWidth
                placeholder="Search draft files by name or path..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
                size="small"
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Select Draft Files ({selectedFiles.length} selected, showing {filteredFiles.length} of {draftFiles.length})
                </Typography>
                <Chip
                  label={filteredFiles.every(f => selectedFiles.includes(f.key)) && filteredFiles.length > 0 ? "Deselect All" : "Select All"}
                  onClick={handleSelectAll}
                  size="small"
                  color="primary"
                  variant="outlined"
                  disabled={filteredFiles.length === 0}
                />
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                {filteredFiles.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No files match "{searchQuery}"
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {filteredFiles.map((file) => (
                    <ListItem
                      key={file.key}
                      button
                      onClick={() => handleFileToggle(file.key)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.key)}
                          onChange={() => handleFileToggle(file.key)}
                          style={{ marginRight: 12 }}
                        />
                        <ListItemText
                          primary={file.name}
                          secondary={`${new Date(file.lastModified).toLocaleDateString()} - ${(file.size / 1024).toFixed(1)} KB`}
                        />
                      </Box>
                    </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </>
          )}
        </Box>
      )}

      {selectedFiles.length > 0 && (
        <Alert severity="info">
          Ready to import <strong>{selectedFiles.length}</strong> draft{selectedFiles.length > 1 ? 's' : ''}
        </Alert>
      )}
    </Box>
  );
};

// Step 2: Select Destination RangeOS
const DestinationSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const rangeosEnvs = await rangeosEnvironmentService.getActiveEnvironments();
        setEnvironments(rangeosEnvs);
      } catch (error) {
        console.error('Failed to load RangeOS environments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, []);

  // Update validation whenever target endpoint changes
  useEffect(() => {
    const targetEndpoint = wizard.getData('targetEndpoint');
    wizard.setStepValidation(1, Boolean(targetEndpoint));
  }, [wizard.getData('targetEndpoint')]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Destination RangeOS Environment
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose the RangeOS environment where the drafts will be imported.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Target RangeOS Environment</InputLabel>
        <Select
          value={wizard.getData('targetEndpoint', '')}
          onChange={(e) => wizard.setData('targetEndpoint', e.target.value)}
          label="Target RangeOS Environment"
        >
          {environments.map((env) => (
            <MenuItem key={env.id} value={env.id}>
              {env.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Alert severity="info">
        The drafts will be created in the selected RangeOS environment as new design drafts.
      </Alert>
    </Box>
  );
};

// Step 3: Configure Import Options
const ConfigureImportStep: React.FC = () => {
  const wizard = useWizard();

  // Step 2 is always valid (optional configuration)
  useEffect(() => {
    wizard.setStepValidation(2, true);
  }, []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure Import Options
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure how the drafts should be imported.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={wizard.getData('addImportedPrefix', false)}
            onChange={(e) => {
              wizard.setData('addImportedPrefix', e.target.checked);
              if (e.target.checked && !wizard.getData('importPrefix')) {
                wizard.setData('importPrefix', '(Imported) ');
              } else if (!e.target.checked) {
                wizard.setData('importPrefix', '');
              }
            }}
          />
        }
        label='Add "(Imported)" prefix to draft names'
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Custom Draft Name Prefix (optional)"
        value={wizard.getData('importPrefix', '')}
        onChange={(e) => {
          wizard.setData('importPrefix', e.target.value);
          wizard.setData('addImportedPrefix', false);
        }}
        placeholder="e.g., (Transferred), Testing-, etc."
        helperText="Custom prefix to add to all imported draft names (overrides the checkbox above)"
        sx={{ mb: 3 }}
      />

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Important:</strong> The imported drafts will include all resource references (VMImages, Charts, etc.) from the source.
        These references may not exist in the target RangeOS instance and may need validation and remapping.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          What will be imported:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Draft metadata (name, description, version)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="All draft states and payloads" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Resource references (may need remapping)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Scenario designer data" />
          </ListItem>
        </List>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Tip:</strong> After importing, use the Draft Validator to check and remap any invalid resource references.
      </Alert>
    </Box>
  );
};

// Step 4: Review and Confirm
const ReviewStep: React.FC = () => {
  const wizard = useWizard();
  const selectedFileNames = wizard.getData('selectedFileNames', []);
  const selectedFiles = wizard.getData('selectedFiles', []);

  // Review step is always valid
  useEffect(() => {
    wizard.setStepValidation(3, true);
  }, []);

  const summarySections: SummarySection[] = [
    {
      title: 'Source',
      items: [
        { label: 'S3 Environment', value: wizard.getData('sourceEnvironment') },
        { label: 'Drafts to Import', value: `${selectedFiles.length} draft${selectedFiles.length > 1 ? 's' : ''}` }
      ]
    },
    {
      title: 'Destination',
      items: [
        { label: 'RangeOS Instance', value: wizard.getData('targetEndpoint') }
      ]
    },
    {
      title: 'Import Options',
      items: [
        { label: 'Name Prefix', value: wizard.getData('importPrefix') || 'None' }
      ]
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Import Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the import configuration before proceeding.
      </Typography>

      <ProgressSummaryPanel sections={summarySections} />

      {selectedFileNames.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Drafts:
          </Typography>
          <List dense>
            {selectedFileNames.map((name: string, idx: number) => (
              <ListItem key={idx}>
                <ListItemText primary={`${idx + 1}. ${name}`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Alert severity="warning" sx={{ mt: 2 }}>
        <strong>Note:</strong> After import, the drafts may contain resource references that don't exist in the target RangeOS instance.
        Use the Draft Validator to check and remap these references before publishing.
      </Alert>
    </Box>
  );
};

export const ImportDraftFromS3Wizard: React.FC<ImportDraftFromS3WizardProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const handleComplete = async (wizardData: Record<string, any>) => {
    const selectedFiles = wizardData.selectedFiles || [];
    const results = [];

    try {
      // Import each draft
      for (const s3Key of selectedFiles) {
        try {
          const response = await rangeosApiService.importDraftFromS3(
            wizardData.targetEndpoint,
            {
              sourceEnvironment: wizardData.sourceEnvironment,
              s3Key,
              draftName: wizardData.importPrefix || undefined
            }
          );

          if (response.success) {
            console.log(`Draft ${s3Key} imported successfully:`, response);
            results.push({ s3Key, success: true, draftId: response.draftId });
          } else {
            results.push({ s3Key, success: false, error: response.message });
          }
        } catch (error: any) {
          console.error(`Failed to import draft ${s3Key}:`, error);
          results.push({ s3Key, success: false, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      console.log(`Imported ${successCount}/${results.length} drafts successfully`);

      if (onComplete && successCount > 0) {
        const firstSuccess = results.find(r => r.success);
        onComplete(firstSuccess?.draftId || 'batch-import-complete');
      }

      if (successCount < results.length) {
        const failures = results.filter(r => !r.success);
        throw new Error(`${failures.length} draft(s) failed to import`);
      }
    } catch (error: any) {
      console.error('Import process encountered errors:', error);
      throw error;
    }
  };

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title="Import Draft Scenarios from S3"
      steps={wizardSteps}
      onComplete={handleComplete}
    >
      <WizardStep stepIndex={0}>
        <SourceSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={1}>
        <DestinationSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={2}>
        <ConfigureImportStep />
      </WizardStep>
      <WizardStep stepIndex={3}>
        <ReviewStep />
      </WizardStep>
    </WizardDialog>
  );
};
